import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { DeliveryType, OrderDirection } from 'webapp/enums/enums';
import { NotificationFilterFrequency, NotificationFilterType } from 'webapp/enums/notifications';
import type { SearchProps } from 'webapp/utils/filter';
import { isNumber } from 'webapp/utils/number';

import type { Notification } from 'webapp/models-custom';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';

export default class FilterSortNotification extends FilterSort<Notification> {
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked override sortProp = 'frequencySortProp';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.ASC;

	override searchProps: SearchProps = {
		title: 3,
		subtitleText: 2,
		scheduleTypeText: 1,
	};

	override settingsNamePrefix = 'notificationFilterSettings';

	constructor(options: FilterSortSettings<Notification>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		this.createFilter<NotificationFilterType | 'all'>({
			id: 1,
			label: 'Type',
			name: 'type',
			selected: selectedFilters[1] as NotificationFilterType[] | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.typeOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((type) => {
					return item.type === type;
				});
			},
		});

		this.createFilter<NotificationFilterFrequency | 'all'>({
			id: 2,
			label: 'Frequency',
			name: 'frequency',
			selected: selectedFilters[2] as NotificationFilterFrequency[] | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.frequencyOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((frequency) => {
					return frequency === 'all' || item.frequency.includes(frequency);
				});
			},
		});

		this.createFilter<string>({
			id: 3,
			label: 'Delivery',
			name: 'delivery',
			selected: selectedFilters[3] as string[] | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.deliveryOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((delivery) => {
					return item.deliveryTypes.includes(+delivery);
				});
			},
		});

		this.createFilter<string>({
			id: 4,
			label: 'Recipients',
			name: 'recipients',
			selected: selectedFilters[4] as string[] | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.recipientOptions;
			},
			filterFn: function (item, selected) {
				if (selected.includes('-1')) {
					// exception: all others
					return !item.isRecipientsOnlyMe;
				} else {
					return selected.some((s) => {
						const castValue = isNumber(s) ? +s : s;
						return !!item.emailRecipients.includes(castValue);
					});
				}
			},
		});
	}

	get typeOptions() {
		const allTypes = [
			NotificationFilterType.METRIC,
			NotificationFilterType.SCORECARD,
			NotificationFilterType.ALERT,
			NotificationFilterType.BOARD,
			NotificationFilterType.LOOP,
			NotificationFilterType.REPORT,
			NotificationFilterType.GOAL,
		];

		const fullList: FilterOption<NotificationFilterType | 'all'>[] = allTypes.map((f) => {
			return { id: f, text: this.mmNotificationsService.getTypeText(f) };
		});

		// only showing existing
		const allItems = this.allItems;
		const list = fullList.filter((item) => {
			return !!allItems.find((i) => i.type === item.id);
		});

		list.unshift({ id: 'all', text: 'All', notCombinable: true });

		return list;
	}

	get frequencyOptions() {
		const allFrequencies = [
			NotificationFilterFrequency.DAILY,
			NotificationFilterFrequency.WEEKLY,
			NotificationFilterFrequency.BIWEEKLY,
			NotificationFilterFrequency.MONTHLY,
			NotificationFilterFrequency.QUARTERLY,
			NotificationFilterFrequency.CONDITIONAL,
			NotificationFilterFrequency.STATUS_CHANGE,
			NotificationFilterFrequency.ANOMALY,
		];
		const fullList: FilterOption<NotificationFilterFrequency | 'all'>[] = allFrequencies.map(
			(f) => {
				return { id: f, text: this.mmNotificationsService.getFrequencyText(f) };
			},
		);

		// only showing existing
		const allItems = this.allItems;
		const list = fullList.filter((item) => {
			return !!allItems.find((i) =>
				i.frequency.includes(item.id as NotificationFilterFrequency),
			);
		});

		list.unshift({ id: 'all', text: 'All', notCombinable: true });

		return list;
	}

	deliveryOptions = [
		// stringify bcos push is 0, same as numeric all
		{ id: 'all', text: 'All', notCombinable: true },
		{ id: String(DeliveryType.EMAIL), text: 'Email' },
		{ id: String(DeliveryType.PUSH), text: 'Mobile' },
		{ id: String(DeliveryType.SLACK), text: 'Slack' },
	];

	get recipientOptions() {
		const allItemCountByRecipientIds: Record<string, number> = {},
			allItemCountByRecipientEmails: Record<string, number> = {};
		this.allItems.forEach((notification) => {
			notification.emailRecipients.forEach((recipient) => {
				const target =
					typeof recipient === 'number'
						? allItemCountByRecipientIds
						: allItemCountByRecipientEmails;
				if (!target[recipient]) {
					target[recipient] = 0;
				}
				target[recipient]++;
			});
		});

		// transfer all to strings
		const list = this.mmUsersService
			.getFilterOptionsForUsers(allItemCountByRecipientIds)
			.map((i) => {
				return { ...i, id: i.id ? String(i.id) : 'all' };
			});

		// add emails
		Object.keys(allItemCountByRecipientEmails)
			.sort()
			.forEach((email) => {
				list.push({ id: email, text: email, used: allItemCountByRecipientEmails[email] });
			});

		return list;
	}
}
